/**
 * Downscale + re-encode an image File in the browser before it is uploaded.
 *
 * Delivery photos are snapped on phones and arrive as 5-10 MB files, which makes
 * the order-submit upload slow enough to hang or drop. Shrinking them here keeps
 * the upload to a few hundred KB so the booking submit succeeds reliably.
 *
 * Fail-safe by design: on any error, unsupported browser, or a result that isn't
 * actually smaller, it returns the ORIGINAL file unchanged. It can only shrink or
 * no-op — it never throws and never blocks the upload.
 *
 * @param {File} file
 * @param {{ maxDimension?: number, quality?: number }} [opts]
 * @returns {Promise<File>} compressed JPEG File, or the original file
 */
export default async function compressImage(file, {maxDimension = 1600, quality = 0.72} = {}) {
  // Not an image (or nothing selected) — leave it alone.
  if (!file || !file.type || file.type.indexOf('image/') !== 0) return file

  // No decoder we can trust for EXIF orientation — skip rather than risk a rotated image.
  if (typeof createImageBitmap !== 'function') return file

  let bitmap
  try {
    bitmap = await createImageBitmap(file, {imageOrientation: 'from-image'})

    let {width, height} = bitmap
    const longest = Math.max(width, height)
    if (longest > maxDimension) {
      const scale = maxDimension / longest
      width = Math.round(width * scale)
      height = Math.round(height * scale)
    }

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return file
    ctx.drawImage(bitmap, 0, 0, width, height)

    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality))

    // Encoding failed, or the "compressed" version is no smaller — keep the original.
    if (!blob || blob.size >= file.size) return file

    const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg'
    return new File([blob], name, {type: 'image/jpeg', lastModified: Date.now()})
  } catch (e) {
    return file
  } finally {
    if (bitmap && typeof bitmap.close === 'function') bitmap.close()
  }
}
